import { NgModule } from '@angular/core';
import { Data, Route, RouterModule } from '@angular/router';
import { ErrorPageComponent } from 'common/components/error-page/error-page.component';
import { environment } from 'common/environments/environment';
import { Auth } from 'private/app/guards/auth.guard';
import { CreateAccount } from 'private/app/guards/createAccount.guard';
import { Login } from 'private/app/guards/login.guard';
import { InboxComponent } from 'private/app/components/inbox/inbox.component';
import { BrandSelectComponent } from './views/brand-select/brand-select.component';
import { CreateEmailValidateComponent } from './views/create-email-validate/createEmailvalidate.component';
import { CallbackComponent } from './views/callback/callback.component';
import { ChangePasswordComponent } from './views/change-password/change-password.component';
import { CoolCalcComponent } from '../app/views/cool-calc/cool-calc.component';
import { CreateAccountInformationComponent } from './views/create-account-information/createAccountInformation.component';
import { CreateAccountLookupComponent } from './views/create-account-lookup/createAccountLookup.component';
import { DashboardComponent } from './views/dashboard/dashboard.component';
import { LoginComponent } from './views/login/login.component';
import { SignOutComponent } from './views/sign-out/signOut.component';
import { ProfileComponent } from './views/profile-landing/profile/profile.component';
import { ProfileLandingComponent } from './views/profile-landing/profile-landing.component';
import { CompanyInfoComponent } from './views/profile-landing/company-info/company-info.component';
import { ManageSubscriptionsComponent } from './views/profile-landing/manage-subscriptions/manage-subscriptions.component';
import { AssociatedDistributorLookup } from 'private/app/components/associated-distributor-lookup/associated-distributor-lookup';
import { CoolCalcTermsAndConditionsComponent } from './views/cool-calc-terms-and-conditions/cool-calc-terms-and-conditions.component';
import { CompanyCode } from './models/company.model';
import { CoolCalTermsGuard } from './guards/cool-calc-terms.guard';
import { TradePartnersGuard } from './guards/trade-partners.guard';
import { accountAdminRoutes } from './../../private/app/account-admin-routes';
import { AppConstants, EnvName } from 'common/app-constants';
import { TradepartnersComponent } from './views/trade-partners/trade-partners.component';
import { PurchaseOrderDetailComponent } from './views/trade-partners/purchase-order-detail/purchase-order-detail.component';
import { PurchaseOrderListComponent } from './views/trade-partners/purchase-order-list/purchase-order-list.component';
import { AcknowledgementComponent } from './views/trade-partners/acknowledgement/acknowledgement.component';
import { AdvancedShipmentComponent } from './views/trade-partners/advanced-shipment/advanced-shipment.component';
import { IdmUserRoles } from './enums/idm-user-roles';
import { OptiCleanSizingSelectionToolComponent } from 'src/private/app/components/opti-clean-sizing-selection-tool/opti-clean-sizing-selection-tool.component';
import { MonthlyCustomerStatementsComponent } from './components/monthly-customer-statements/monthly-customer-statements/monthly-customer-statements.component';
import { MonthlyCustomerStatementGuard } from './guards/monthly-customer-statements.guard';
import { DeleteMyAccountComponent } from './views/profile-landing/delete-my-account/delete-my-account.component';
import { DeleteAccountPageComponent } from './views/delete-account-page/delete-account-page.component';
import { ConnectedPortalRedirectComponent } from './views/connected-portal-redirect/connected-portal-redirect.component';
import { accountAdminRoutesProd } from './account-admin-routes-prod';
import { SapModelNumberDecoderComponent } from './components/sap-model-number-decoder/sap-model-number-decoder.component';
import { SapModelNumberDecoderGuard } from './guards/sap-model-number-decoder.guard';
import { WhatsNewResultsComponent } from './components/whats-new-results/whats-new-results.component';

export interface PrivateAppRouteData extends Data {
    allowed?: boolean,
    tridionFeatures?: string[],
    envFeature?: boolean,
    allowedCompanyCodes?: CompanyCode[],
    viewType?: CompanyCode,
    accessRoles?: IdmUserRoles[]
}

export interface PrivateAppRoute extends Route {
    data?: PrivateAppRouteData;
    children?: PrivateAppRoute[]
}

const accountAdminRoutesPage = (environment.envName === EnvName.Production || environment.envName === EnvName.Future) ? accountAdminRoutesProd : accountAdminRoutes;

const routes: PrivateAppRoute[] = [
    ...accountAdminRoutesPage,
    {
        path: '',
        component: DashboardComponent,
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'Pages',
        loadChildren: () => import('private/app/views/frame-content/frame-content.module').then((m) => m.FrameContentModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'brand-select',
        component: BrandSelectComponent
    },
    {
        path: 'ArchivedPages',
        loadChildren: () => import('private/app/views/frame-content/frame-content.module').then((m) => m.FrameContentModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'login',
        component: LoginComponent,
        canActivate: [Login]
    },
    {
        path: 'logout',
        component: LoginComponent
    },
    {
        path: 'login-error',
        component: LoginComponent
    },
    {
        path: 'callback',
        component: CallbackComponent
    },
    {
        path: 'secure-callback',
        component: CallbackComponent
    },
    {
        path: 'create-account-information',
        component: CreateAccountInformationComponent
    },
    {
        path: 'delete-account-page',
        component: DeleteAccountPageComponent
    },
    {
        path: 'create-account-email-validate',
        component: CreateEmailValidateComponent,
        canActivate: [CreateAccount]
    },
    {
        path: 'create-account-company-lookup',
        component: CreateAccountLookupComponent,
        canActivate: [CreateAccount]
    },
    {
        path: 'associated-distributor-lookup',
        component: AssociatedDistributorLookup,
        canActivate: [CreateAccount]
    },
    {
        path: 'whats-new-results',
        component: WhatsNewResultsComponent,
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'change-password',
        component: ChangePasswordComponent,
        canActivate: [Auth]
    },
    {
        path: 'profile',
        component: ProfileLandingComponent,
        canActivate: [Auth],
        children: [
            {
                path: '',
                redirectTo: 'profile',
                pathMatch: 'full'
            },
            {
                path: 'profile',
                component: ProfileComponent
            },
            {
                path: 'company-info',
                component: CompanyInfoComponent
            },
            {
                path: 'manage-subscriptions',
                component: ManageSubscriptionsComponent
            },
            {
                path: 'delete-account',
                component: DeleteMyAccountComponent
            }
        ]
    },
    {
        path: 'products/parts/parts-catalog',
        loadChildren: () => import('common/views/parts/parts.module').then((m) => m.PartsModule),
        canActivate: [Auth]
    },
    {
        path: 'marketing/sales-tools/opticlean-tool',
        component: OptiCleanSizingSelectionToolComponent,
        canActivate: [Auth]
    },
    {
        path: 'support/pre-sale-support/cool-calc',
        component: CoolCalcComponent,
        canActivate: [Auth, CoolCalTermsGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'ordering/reports/customer-statements',
        component: MonthlyCustomerStatementsComponent,
        canActivate: [Auth, MonthlyCustomerStatementGuard],
        data: { accessRoles: [IdmUserRoles.MONTHLYCUSTOMERSTATEMENTS] }
    },
    {
        path: 'support/post-sale-support/model-number-decoders/sap-part-number-decoder',
        component: SapModelNumberDecoderComponent,
        canActivate: [Auth, SapModelNumberDecoderGuard],
        data: { title: 'SAP Part Number Decoder' }
    },
    {
        path: 'trade-partners',
        component: TradepartnersComponent,
        canActivate: [Auth],
        canActivateChild: [TradePartnersGuard],
        data: {
            headerData: 'TRADE_PARTNERS.LANDING',
            envFeature: environment.features.tradePartner
        },
        children: [
            {
                path: '',
                component: PurchaseOrderListComponent,
                pathMatch: 'full'
            },
            {
                path: ':id',
                component: PurchaseOrderDetailComponent,
                data: { headerData: 'TRADE_PARTNERS.PO_DETAIL' }
            },
            {
                path: ':id/confirm',
                component: AcknowledgementComponent,
                title: 'Purchase Order Acknowledgement',
                data: { headerData: 'TRADE_PARTNERS.ACKNOWLEDGE.HEADER_DATA' }
            },
            {
                path: ':id/advanced-shipment',
                component: AdvancedShipmentComponent,
                title: 'Advanced Shipment Acknowledgement',
                data: { headerData: 'TRADE_PARTNERS.ADVANCED_SHIPMENT' }
            }
        ]
    },
    {
        path: 'support/pre-sale-support/cool-calc/terms',
        component: CoolCalcTermsAndConditionsComponent
    },
    {
        path: 'search',
        loadChildren: () => import('private/app/views/private-search/private-search.module').then((m) => m.PrivateSearchModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'products/dashboards/productcatalog',
        loadChildren: () => import('private/app/views/products/products.module').then((m) => m.ProductsModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: AppConstants.PrivatePartsLiteratureSearchURL,
        loadChildren: () => import('common/views/product-literature/product-literature.module').then((m) => m.ProductLiteratureModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'support/literature/productliterature',
        loadChildren: () => import('common/views/product-literature/product-literature.module').then((m) => m.ProductLiteratureModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'products/detail',
        loadChildren: () => import('common/views/product-details/product-details.module').then((m) => m.ProductDetailsModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'sign-out',
        component: SignOutComponent
    },
    {
        path: 'inbox',
        component: InboxComponent
    },
    {
        path: 'error',
        component: ErrorPageComponent,
        canActivate: [Auth]
    },
    {
        path: 'cp',
        component: ConnectedPortalRedirectComponent,
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    },
    {
        path: '**',
        loadChildren: () => import('private/app/views/trid-content-page/trid-content-page.module').then((m) => m.TridionContentPageModule),
        canActivate: [Auth],
        runGuardsAndResolvers: 'always'
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
    exports: [RouterModule]
})
export class AppRoutingModule { }
